/** @jsxRuntime automatic */
/** @jsxImportSource react */
/**
 * Trends › Elevation (docs/CONTRACT-fleet-governance.md sections 7.9, 9.1 and 12.3): elevations granted and denied per
 * day (two sparklines and a table of days with the number of distinct operators, counted at each day's close), and the
 * denials by reason. A day opens that day's elevation rows; a total or a reason opens the range's.
 */
import { KeyRound } from 'lucide-react';
import { ELEVATION_CHOICES, trendsApi, trendsExportHref, type GovElevation } from '../../lib/trends';
import { EmptyState, Grid, Panel, Sparkline, Table, type Column } from '../../ui';
import { count } from '../../ui/format';
import { useLoad } from '../pulse/common';
import { ExportLink, Facts } from '../fleet/parts';
import { SectionError, ShowRows, useClientSort, type SectionProps } from './parts';

type Day = GovElevation['days'][number];
type Reason = GovElevation['deniedReasons'][number];

export function TrendsElevation({ query, filter, onShowRows }: SectionProps) {
  const { load, reload, reloading } = useLoad(() => trendsApi.elevation(query), `elevation|${JSON.stringify(query)}`);
  const answer = load.state === 'ready' ? load.data : null;
  const days = useClientSort([...(answer?.days ?? [])].reverse(), (row, key) => (key === 'day' ? row.day : row[key as 'granted' | 'denied' | 'operators']), ['day']);
  const reasons = useClientSort(answer?.deniedReasons ?? [], (row, key) => (key === 'reason' ? row.reason : row.count), ['reason']);

  const open = (title: string, choiceIndex: number | null, day: string | null) => {
    if (!answer) return;
    onShowRows({
      title,
      choices: choiceIndex === null ? ELEVATION_CHOICES : [ELEVATION_CHOICES[choiceIndex]!],
      app: filter.app || null,
      range: { from: answer.range.from, to: answer.range.to },
      day,
    });
  };

  const dayColumns: Array<Column<Day>> = [
    { key: 'day', header: 'Day', sortable: true, cell: (row) => row.day },
    { key: 'granted', header: 'Granted', numeric: true, sortable: true, cell: (row) => count(row.granted) },
    { key: 'denied', header: 'Denied', numeric: true, sortable: true, cell: (row) => count(row.denied) },
    { key: 'operators', header: 'Operators', numeric: true, sortable: true, cell: (row) => (row.operators === null ? '—' : count(row.operators)) },
  ];
  const reasonColumns: Array<Column<Reason>> = [
    { key: 'reason', header: 'Reason', sortable: true, cell: (row) => row.reason },
    { key: 'count', header: 'Denials', numeric: true, sortable: true, cell: (row) => count(row.count) },
  ];

  return (
    <Panel title="Elevation" description="Administrator elevations granted and denied; a day opens its audit rows" actions={<ExportLink href={trendsExportHref('elevation', filter)} />}>
      {load.state === 'failed' ? <SectionError title="The elevations could not be loaded" error={load.error} onRetry={reload} retrying={reloading} /> : (
        <div className="ui-form">
          {answer ? (
            <Facts label="Elevation totals" rows={[
              ['Granted', <span className="ui-chips"><span>{count(answer.granted)}</span><Sparkline values={answer.days.map((day) => day.granted)} label="Elevations granted per day" /><ShowRows onClick={() => open('Elevations granted', 0, null)} /></span>],
              ['Denied', <span className="ui-chips"><span>{count(answer.denied)}</span><Sparkline values={answer.days.map((day) => day.denied)} label="Elevations denied per day" tone={answer.denied > 0 ? 'bad' : 'data'} /><ShowRows onClick={() => open('Elevations denied', 1, null)} /></span>],
            ]} />
          ) : null}
          <Grid cols={2}>
            <div className="ui-table-box">
              <Table
                label="Elevations per day"
                columns={dayColumns}
                rows={days.rows}
                rowKey={(row) => row.day}
                loading={load.state === 'loading'}
                skeletonRows={4}
                stickyHeader={false}
                onRowClick={(row) => open(`Elevations on ${row.day}`, null, row.day)}
                sort={days.sort}
                empty={<EmptyState compact icon={KeyRound} title="No elevations in this range">Granted and denied elevations appear here by day.</EmptyState>}
              />
            </div>
            <div className="ui-table-box">
              <Table
                label="Denials by reason"
                columns={reasonColumns}
                rows={reasons.rows}
                rowKey={(row) => row.reason}
                loading={load.state === 'loading'}
                skeletonRows={2}
                stickyHeader={false}
                onRowClick={(row) => open(`Elevations denied · ${row.reason}`, 1, null)}
                sort={reasons.sort}
                empty={<p className="ui-para ui-quiet">No elevation was denied in this range.</p>}
              />
            </div>
          </Grid>
        </div>
      )}
    </Panel>
  );
}
